import React, { useState } from 'react';
import { DateTime, Numberbox, SectionPanel } from 'ui';
import { MainGridContainer } from 'ui/common/main-grid-container/main-grid-container';
import { parseAsDateUTC, createUTCDate, dateToUTC, createFormatters } from 'app/infrastructure/data/date';
import { MetroLineSection } from 'app/components/metro-line/metro-line-section';
import { WithMetroLineWrappedComponentProps } from 'app/components/metro-line/types';
import { SelectSingle } from 'app/area/shared/translated-components';

import { ComplexValue, LoadOptions } from 'app/types-shared';

import { DashboardAdminTranslation } from '../../constants';
import { DashboardSectionType } from '../metroline/types';
import { endpoints } from '../endpoints';
import { DashboardItem, DashboardItemValidation } from '../types-store';

const sequenceId = 'sequence';
const disabledId = 'disabled';
const accessTypeId = 'accessType';

interface DashboardDetailsProps extends WithMetroLineWrappedComponentProps<DashboardSectionType> {
  dashboardItem: DashboardItem;
  updateItem: (item: Partial<DashboardItem>) => void;
  options: {
    [key: string]: {
      fetching: boolean;
      codes?: ComplexValue<number | string>[];
    };
  };
  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;

  locale: string;
  validation: DashboardItemValidation;
  translation: DashboardAdminTranslation;

  section: string;
}

export const DashboardDetails: React.FC<DashboardDetailsProps> = ({
  dashboardItem, updateItem,
  options, loadOptions,
  locale, validation, translation,
  section, getRef, shouldRenderSection
}) => {

  const [accessTypeOptions, setAccessTypeOptions] = useState<LoadOptions>();

  const onSequenceChange = (value: number) => {
    updateItem({ sequence: value });
  };

  const onDisabledChange = (value: Date) => {
    updateItem({ disabled: value });
  };

  const onAccessTypeChange = (value: ComplexValue<number>) => {
    updateItem({ accessType: value });
  };

  const onAccessTypeLoadOptions = (loadOptionsParams: LoadOptions) => {
    loadOptions(accessTypeId, endpoints.accessTypeOptions, loadOptionsParams);
    setAccessTypeOptions(loadOptionsParams);
  };

  return <MetroLineSection<DashboardSectionType>
    section={section}
    sectionType={DashboardSectionType.Details}
    getRef={getRef}
    shouldRenderSection={shouldRenderSection}>
    <div ref={getRef(DashboardSectionType.Details)}>
      <SectionPanel
        className="admin-paper-top-panel"
        componentId={DashboardSectionType.Details}
        validation={undefined}
        info={{ text: translation.details }}
        label={{ visible: true, text: translation.details }} />
      <MainGridContainer>
        <div className="row">
          <div className="col-lg-6 col-sm-12">
            <Numberbox
              componentId={sequenceId}
              label={translation ? { text: translation.sequence } : undefined}
              value={dashboardItem?.sequence}
              onChange={onSequenceChange}
              useThousand=" "
              maxValue={999999999}
              locale={locale}
              validation={validation.sequence}
              textInvalidRangeError={translation.must_be_within_range}
            />
          </div>
          <div className="col-lg-6 col-sm-12">
            <DateTime
              componentId={disabledId}
              className="dateTime"
              value={dashboardItem?.disabled}
              label={translation ? { text: translation.disabled } : undefined}
              useTime={false}
              parseDate={parseAsDateUTC}
              createDate={createUTCDate}
              cloneDate={dateToUTC}
              {...createFormatters(locale)}
              locale={locale}
              onChange={onDisabledChange}
              captionWrapper={'form'}
              validation={validation.disabled}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-lg-6 col-sm-12">
            <SelectSingle
              componentId={accessTypeId}
              className={undefined}
              label={translation ? { text: translation.access_mode, required: true } : undefined}
              valueItem={dashboardItem?.accessType}
              onChange={onAccessTypeChange}
              validation={validation && validation[accessTypeId]}

              options={options && options[accessTypeId]?.codes as ComplexValue<number>[] || []}
              fetching={options && options[accessTypeId]?.fetching || false}
              loadOptions={accessTypeOptions}
              onLoadOptions={onAccessTypeLoadOptions}

              placeholder={translation?.search} />
          </div>
        </div>
      </MainGridContainer>
    </div>
  </MetroLineSection>;
};
